import { useEffect, useRef, useState, type ReactNode } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';

type Delay = 0 | 1 | 2 | 3 | 4;

interface RevealProps {
  readonly children: ReactNode;
  /** Escalón del stagger. Mapea a las clases .stagger-N del stylesheet. */
  readonly delay?: Delay;
  readonly className?: string;
  /** Etiqueta del elemento contenedor. Por defecto `div`. */
  readonly as?: 'div' | 'section' | 'article' | 'li';
}

/**
 * Entrada con fade-up al hacer scroll.
 *
 * Regla de oro: **el contenido nunca puede quedarse invisible.** El estado
 * inicial es VISIBLE y sólo se oculta cuando el efecto confirma, ya en el
 * cliente, que (a) hay soporte de IntersectionObserver, (b) el usuario no pidió
 * reducir movimiento y (c) el elemento está por debajo del fold. Consecuencias:
 *
 *  - Sin JavaScript, o si el bundle falla → todo se ve, sin animación.
 *  - Con `prefers-reduced-motion` → todo se ve, sin animación.
 *  - Lo que ya está en pantalla al cargar → se ve, sin parpadeo.
 *
 * El patrón inverso (ocultar en el render inicial y revelar después) es la causa
 * habitual de landings que aparecen en blanco cuando algo falla — y para un
 * buscador, de una página cuyo contenido queda en opacity: 0.
 *
 * Se implementa a mano en vez de con Framer Motion / GSAP a propósito: son ~30–70 KB
 * de JavaScript de terceros y una dependencia más en la cadena de suministro
 * para lo que aquí son dos transiciones CSS. El brief de diseño sugería esas
 * librerías; se prescinde de ellas por superficie de ataque y peso.
 */
export function Reveal({
  children,
  delay = 0,
  className = '',
  as: Tag = 'div',
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [state, setState] = useState<'static' | 'hidden' | 'shown'>('static');
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (prefersReducedMotion) return;
    if (typeof IntersectionObserver === 'undefined') return;

    // Sólo animamos lo que aún no se ha visto. Si el elemento ya está en el
    // viewport, ocultarlo para animarlo produciría un parpadeo.
    const isBelowFold = node.getBoundingClientRect().top > window.innerHeight;
    if (!isBelowFold) return;

    setState('hidden');

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setState('shown');
            // `once: true` — animamos una sola vez y soltamos el observer.
            observer.disconnect();
          }
        }
      },
      { threshold: 0.15 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [prefersReducedMotion]);

  const motionClasses =
    state === 'static'
      ? ''
      : state === 'hidden'
        ? `reveal stagger-${delay}`
        : `reveal reveal-visible stagger-${delay}`;

  return (
    <Tag
      ref={ref as never}
      className={`${className} ${motionClasses}`.trim()}
    >
      {children}
    </Tag>
  );
}
